import type * as THREE from 'three';
import { heroName, type CampaignState, type ItemInstance, type PlayerState } from '@somnhold/sim';
import { audio } from '../audio/sfx.ts';
import { t } from '../i18n.ts';
import { h } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';
import { thumbLater } from '../ui/thumbs.ts';
import { RUNE, itemName, itemThumbKey } from './gear-sheet.ts';

/*
 * «A friend sent you…»: a card per friend who sent something, with the piece itself (its picture in its rarity's frame,
 * its name in its rarity's color) or, for several, the best few in a row. A tap opens the bag on it. Cards stack
 * (three at most, the rest folded into the last), stay while hovered and go by themselves; nothing blocks the board.
 */

const SHOW_MS = 8000;
const MAX_CARDS = 3;
const MAX_PICS = 4;
const RARITY_ORDER: Record<string, number> = { unique: 0, veryRare: 1, rare: 2, uncommon: 3, common: 4 };

export class GiftNotice {
  readonly el = h('div.gift-notices', { role: 'status', 'aria-live': 'polite', 'data-test': 'gift-notices' });

  constructor(
    private renderer: THREE.WebGLRenderer,
    private open: (item: number) => void,
  ) {}

  /** Announces these gifts, one card per sender. */
  show(s: CampaignState, me: PlayerState, gifts: ItemInstance[]): void {
    const by = new Map<string, ItemInstance[]>();
    for (const g of gifts) by.set(g.from ?? '', [...(by.get(g.from ?? '') ?? []), g]);
    const groups = [...by.entries()];
    // More senders than cards: the last card speaks for the rest.
    const shown = groups.slice(0, MAX_CARDS);
    const rest = groups.slice(MAX_CARDS).reduce((a, [, xs]) => a + xs.length, 0);
    for (const [i, [pid, items]] of shown.entries()) {
      const who = s.players.find((x) => x.id === pid);
      this.card(me, who ? heroName(who) : '—', who?.color, items, i === shown.length - 1 ? rest : 0);
    }
    while (this.el.children.length > MAX_CARDS) this.el.firstElementChild?.remove();
    audio.play('mend', 0.35);
  }

  private card(me: PlayerState, who: string, color: string | undefined, items: ItemInstance[], more: number): void {
    const cls = me.hero.heroClass;
    const best = items.slice().sort((x, y) => (RARITY_ORDER[x.rarity] ?? 5) - (RARITY_ORDER[y.rarity] ?? 5) || (y.power ?? 0) - (x.power ?? 0));
    const top = best[0] as ItemInstance;
    const one = items.length === 1;
    const pic = (item: ItemInstance, size: number): HTMLElement => {
      const img = h('img.loading', { alt: '', draggable: 'false' }) as HTMLImageElement;
      const box = h('span.gn-pic', { style: `--rune:${RUNE[item.rarity]}` }, img);
      thumbLater(this.renderer, itemThumbKey(item, cls), size, (url) => ((img.src = url), img.classList.remove('loading')), () => img.isConnected || !img.ownerDocument);
      return box;
    };
    const close = h('button.gn-close', { type: 'button', 'aria-label': t('bastion.close'), html: ICONS.close });
    const card = h(
      'div.gift-notice',
      { style: `--rune:${RUNE[top.rarity]};${color ? `--who:${color}` : ''}`, 'data-test': 'gift-notice' },
      h('button.gn-body', { type: 'button', 'aria-label': `${one ? t('gift.toast', { who }) : t('gift.toastMany', { who, n: items.length })} · ${t('gift.see')}` },
        one ? pic(top, 96) : h('span.gn-pics', {}, ...best.slice(0, MAX_PICS).map((x) => pic(x, 64))),
        h(
          'span.gn-text',
          {},
          h('span.gn-who', {}, h('span', { html: ICONS.chest }), one ? t('gift.toast', { who }) : t('gift.toastMany', { who, n: items.length })),
          one
            ? h('b.gn-name', {}, itemName(top, cls), top.plus ? ` +${top.plus}` : '')
            : h('b.gn-name', {}, t('gift.best', { what: itemName(top, cls) })),
          h('span.gn-sub', {}, one ? `${t(`rarity.${top.rarity}`)} · ${t(`slot.${top.slot}`)}` : t('gift.see'), more ? ` · ${t('gift.more', { n: more })}` : ''),
        ),
      ),
      close,
    );
    let timer = 0;
    const go = (): void => {
      window.clearTimeout(timer);
      card.classList.add('out');
      window.setTimeout(() => card.remove(), 220);
    };
    const wait = (): void => {
      window.clearTimeout(timer);
      timer = window.setTimeout(go, SHOW_MS);
    };
    (card.querySelector('.gn-body') as HTMLElement).onclick = () => {
      go();
      this.open(top.id);
    };
    close.onclick = go;
    // Read at leisure: hovering it holds it.
    card.onpointerenter = () => window.clearTimeout(timer);
    card.onpointerleave = wait;
    this.el.append(card);
    wait();
  }

  /** Gone without a trace (the bastion closed). */
  dispose(): void {
    this.el.replaceChildren();
  }
}
